import React, { CSSProperties, useEffect, useState } from "react";
import styled from "styled-components";
import { useChangeEffect, useClickOutside, useIsMobile } from "@utils/hooks";
import { ChevronDownIcon } from "@ui/components/icons";
import { fadeIn } from "@ui/styles/keyframes";
import { Z } from "@ui/styles/zIndex";
import { useModalTools } from "./modalContext";

export interface BottomSheetModalProps extends React.HTMLAttributes<HTMLDivElement> {
	bottomSheetModalWrapperStyle?: CSSProperties;
	bottomSheetModalContentStyle?: CSSProperties;
	onOpenChange?: (open: boolean) => void;
	closeOnSelect?: boolean;
	position?: "top" | "bottom";
	align?: "left" | "right";
	fixed?: "right" | "center";
	bottomSheetMode?: boolean;
	manualOpen?: boolean;
	bottomSheetModalHeight?: number | undefined;
	reportClosed: () => void;
}

export const BottomSheetModal: React.FC<BottomSheetModalProps> = ({
	bottomSheetModalWrapperStyle,
	bottomSheetModalContentStyle,
	onOpenChange,
	closeOnSelect,
	fixed,
	bottomSheetMode,
	children,
	reportClosed,
	manualOpen = false,
	...htmlProps
}) => {
	const [opened, setOpened] = useState(manualOpen);
	const [, setBottomSheetModalCenter] = useState(0);
	const { activeModalsCount } = useModalTools();
	const isMobile = useIsMobile();
	const [height, setHeight] = useState<number>(0);
	const isBottomSheet = isMobile;
	const bottomSheetModalRef = useClickOutside((e) => {
		if (opened) {
			e.preventDefault();
			setOpened(false);
		}
	});

	useEffect(() => {
		if (activeModalsCount > 0) {
			setOpened(false);
		}
	}, [activeModalsCount]);

	useChangeEffect(() => {
		onOpenChange?.(opened);
		if (!opened) reportClosed();
	}, [opened]);

	useEffect(() => {
		const resizeListener = (): void => {
			if (!bottomSheetModalRef.current) {
				return;
			}
			const { left, width } = bottomSheetModalRef.current.getBoundingClientRect();
			setBottomSheetModalCenter(left + width / 2);
		};
		resizeListener();
		window.addEventListener("resize", resizeListener);
		return () => window.removeEventListener("resize", resizeListener);
	}, []);

	useEffect(() => {
		if (opened && bottomSheetModalRef) {
			setHeight(window.innerHeight);
		}
	}, [opened]);
	return isBottomSheet && opened ? (
		<>
			<RestOfScreen height={height} />
			<Container {...htmlProps} zIndexActive={opened && !isBottomSheet} ref={bottomSheetModalRef}>
				<StyledCloseIcon onClick={() => setOpened(false)} />
				<ContentWrapper>{children}</ContentWrapper>
			</Container>
		</>
	) : (
		<></>
	);
};

const RestOfScreen = styled.div.withConfig({
	shouldForwardProp: (prop) => prop !== "height",
})<{ height: number }>`
	position: fixed;
	z-index: 98;
	top: 0;
	left: 0;
	right: 0;
	min-height: ${({ height }) => height || undefined}px;
	max-height: ${({ height }) => height || undefined}px;
	background-color: black;
	opacity: 0.7;
`;

const Container = styled.div.withConfig({
	shouldForwardProp: (prop) => prop !== "zIndexActive",
})<{ zIndexActive: boolean }>`
	position: fixed;
	bottom: 0;
	left: 0;
	right: 0;
	${({ zIndexActive }) => zIndexActive && `z-index: ${Z.menu}`};
	min-width: 100%;
	animation: ${fadeIn} 0.1s linear;
	z-index: 99;
	background-color: #ffffff !important;
	height: -webkit-fit-content;
	height: -moz-fit-content;
	height: fit-content;
	display: -webkit-box;
	display: -webkit-flex;
	display: -ms-flexbox;
	display: flex;
	-webkit-flex: 1;
	-ms-flex: 1;
	flex: 1;
	width: 100%;
	border-top-left-radius: 10px;
	border-top-right-radius: 10px;
	padding-top: 20px;
	padding-left: 20px;
	padding-right: 20px;
	padding-bottom: 10px;
	overflow-x: hidden;
	opacity: 1 !important;
`;

const StyledCloseIcon = styled(ChevronDownIcon)`
	position: absolute;
	top: 14px;
	right: 30px;
	z-index: 100;
`;

const ContentWrapper = styled.div`
	margin-top: 20px;
	display: -webkit-box;
	display: -webkit-flex;
	display: -ms-flexbox;
	display: flex;
	-webkit-flex: 1;
	-ms-flex: 1;
	flex: 1;
	width: 100%;
	padding-top: 20px;
	padding-left: 20px;
	padding-right: 20px;
	padding-bottom: 20px;
	overflow-x: hidden;
`;
